import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { MediaOrganization, MediaOrgType } from "../types/media";
import { ORG_ACCESS_INVALIDATES } from "./mediaInvalidation";
import {
    MediaFunctionError,
    mediaErrorFromBody,
    mediaErrorFromInvoke,
    messageFromInvokeError,
    messageFromInvokeBody,
} from "./mediaInvokeError";
import { agreementUploadCheck } from "../utils/mediaAgreement";

// ============================================================================
// Media Organizations data layer (federation / admin webapp)
// Mirrors the News module pattern: one list query + focused mutations.
// Replaces the former read-only dropdown hook in useMediaPersonnel.ts.
// ============================================================================

export interface MediaOrganizationFilters {
    searchQuery?: string;
    orgType?: MediaOrgType | string | null;
    isApproved?: boolean | null; // agrément granted?
    isActive?: boolean | null; // participation authorised?
}

interface MediaOrganizationListResult {
    data: MediaOrganization[];
    total: number;
}

export interface CreateOrganizationPayload {
    name: string;
    name_ar?: string | null;
    org_type: MediaOrgType;
    contact_email?: string | null;
    contact_phone?: string | null;
    website?: string | null;
    address?: string | null;
    address_ar?: string | null;
    notes?: string | null;
}

export type UpdateOrganizationPayload = Partial<CreateOrganizationPayload>;

export interface ProvisionOrgLoginResult {
    email: string;
    password: string;
    user_id: string;
    reset?: boolean;
    email_sent?: boolean;
    email_error?: string;
}

const ORG_LIST_KEY = "media-organizations";

// Foreign-table count → each row carries `journalists: [{ count }]`; the
// query narrows it to live journalists (`journalists.deleted_at` is null —
// an embedded filter restricts the rows the count aggregates).
// `deleted_at` itself comes with `*`.
// `team` is only populated for account_kind='club' orgs (team_id set).
const ORG_SELECT = `
    *,
    journalists:media_personnel!organization_id(count),
    team:teams!team_id(id, name, name_ar, logo_url)
`;

/**
 * List media organizations with an exact count and optional search / filters.
 * Each row is enriched with `journalist_count`.
 */
export function useMediaOrganizations(
    filters: MediaOrganizationFilters = {},
    { enabled = true }: { enabled?: boolean } = {}
) {
    return useQuery<MediaOrganizationListResult>({
        enabled,
        queryKey: [ORG_LIST_KEY, filters],
        queryFn: async () => {
            let query = supabase
                .from("media_organizations")
                .select(ORG_SELECT, { count: "exact" })
                // Deleted organismes leave every league-facing list; their
                // past matches still render them through other views.
                .is("deleted_at", null)
                .is("journalists.deleted_at", null)
                .order("name", { ascending: true });

            if (filters.orgType) {
                query = query.eq("org_type", filters.orgType);
            }
            if (filters.isApproved !== undefined && filters.isApproved !== null) {
                query = query.eq("is_approved", filters.isApproved);
            }
            if (filters.isActive !== undefined && filters.isActive !== null) {
                query = query.eq("is_active", filters.isActive);
            }
            if (filters.searchQuery?.trim()) {
                const q = filters.searchQuery.trim();
                query = query.or(
                    `name.ilike.%${q}%,name_ar.ilike.%${q}%,contact_email.ilike.%${q}%`
                );
            }

            const { data, error, count } = await query;
            if (error) {
                throw new Error(error.message);
            }

            const orgs = (data || []).map((row: any) => {
                const { journalists, ...org } = row;
                const journalist_count =
                    Array.isArray(journalists) && journalists.length > 0
                        ? journalists[0]?.count ?? 0
                        : 0;
                return { ...org, journalist_count } as MediaOrganization;
            });

            return { data: orgs, total: count || 0 };
        },
        staleTime: 30000,
    });
}

/**
 * Create a new media organization (contact info only — agrément / participation
 * are federation-only and set through their dedicated mutations).
 */
export function useCreateOrganization() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CreateOrganizationPayload) => {
            const { data, error } = await supabase
                .from("media_organizations")
                .insert({
                    name: payload.name,
                    name_ar: payload.name_ar ?? null,
                    org_type: payload.org_type,
                    contact_email: payload.contact_email ?? null,
                    contact_phone: payload.contact_phone ?? null,
                    website: payload.website ?? null,
                    address: payload.address ?? null,
                    address_ar: payload.address_ar ?? null,
                    notes: payload.notes ?? null,
                })
                .select()
                .single();

            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ORG_LIST_KEY] });
        },
    });
}

/**
 * Update an organization's editable contact / identity info.
 */
export function useUpdateOrganization() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            payload,
        }: {
            id: string;
            payload: UpdateOrganizationPayload;
        }) => {
            const update: Record<string, unknown> = {
                updated_at: new Date().toISOString(),
            };
            const keys: (keyof UpdateOrganizationPayload)[] = [
                "name",
                "name_ar",
                "org_type",
                "contact_email",
                "contact_phone",
                "website",
                "address",
                "address_ar",
                "notes",
            ];
            for (const key of keys) {
                if (payload[key] !== undefined) update[key] = payload[key];
            }

            const { data, error } = await supabase
                .from("media_organizations")
                .update(update)
                .eq("id", id)
                .select()
                .single();

            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ORG_LIST_KEY] });
        },
    });
}

/**
 * Grant (or revoke) the agrément: set is_approved / approved_by / approved_at.
 * The admin actor is privileged, so the federation-fields DB trigger lets this through.
 */
export function useGrantAgrement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            approved,
            approvedBy,
        }: {
            id: string;
            approved: boolean;
            approvedBy: string;
        }) => {
            const update = approved
                ? {
                      is_approved: true,
                      approved_by: approvedBy,
                      approved_at: new Date().toISOString(),
                  }
                : { is_approved: false, approved_by: null, approved_at: null };

            const { data, error } = await supabase
                .from("media_organizations")
                .update(update)
                .eq("id", id)
                .select()
                .single();

            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            // Agrément state gates per-league access (see hooks/mediaInvalidation.ts).
            ORG_ACCESS_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

/**
 * Reclassify an org between 'press' and 'club', optionally linking a team.
 * The admin actor is privileged, so the federation-fields DB trigger lets
 * this through directly (non-privileged writers have account_kind/team_id
 * frozen). A unique index on team_id rejects linking a team that already
 * has a club media desk (surfaced to the caller as a Postgres error).
 */
export function useReclassifyOrgKind() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            accountKind,
            teamId,
        }: {
            id: string;
            accountKind: "press" | "club";
            teamId: string | null;
        }) => {
            const { data, error } = await supabase
                .from("media_organizations")
                .update({
                    account_kind: accountKind,
                    team_id: accountKind === "club" ? teamId : null,
                })
                .eq("id", id)
                .select()
                .single();

            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ORG_LIST_KEY] });
        },
    });
}

/**
 * Lightweight teams lookup for the reclassify picker — reads the publicly
 * readable `teams` table, optionally narrowed by a name search. `enabled`
 * lets callers defer the fetch until the picker UI is actually shown
 * (the table holds ~1.5k rows, so this is search-driven rather than a
 * one-shot full list).
 */
export function useTeamsList(search?: string, enabled: boolean = true) {
    return useQuery({
        queryKey: ["teams-list", search],
        enabled,
        queryFn: async () => {
            let q = supabase
                .from("teams")
                .select("id, name, name_ar, logo_url")
                .order("name", { ascending: true })
                .limit(50);
            if (search?.trim()) {
                q = q.or(
                    `name.ilike.%${search.trim()}%,name_ar.ilike.%${search.trim()}%`
                );
            }
            const { data, error } = await q;
            if (error) throw new Error(error.message);
            return data as {
                id: string;
                name: string;
                name_ar?: string | null;
                logo_url?: string | null;
            }[];
        },
        staleTime: 60000,
    });
}

/**
 * Toggle participation (is_active) — the federation's authorisation for the org
 * to participate.
 */
export function useToggleParticipation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            isActive,
        }: {
            id: string;
            isActive: boolean;
        }) => {
            const { data, error } = await supabase
                .from("media_organizations")
                .update({ is_active: isActive })
                .eq("id", id)
                .select()
                .single();

            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ORG_LIST_KEY] });
        },
    });
}

/**
 * Upload (or replace) the agrément PDF into the private `media-documents` bucket
 * and store the object key in `agreement_url`.
 */
export function useUploadAgreement() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, file }: { id: string; file: File }) => {
            // The bucket only takes PDF / JPEG / PNG ≤ 10 MB; the drawer checks
            // first, this keeps the declared type honest (never "pdf" for a JPEG).
            const kind = agreementUploadCheck(file);
            if (kind.ok === false) throw new Error(`AGREEMENT_FILE_${kind.reason.toUpperCase()}`);
            const path = `org-agreements/${id}-${Date.now()}.${kind.ext}`;

            const { error: uploadError } = await supabase.storage
                .from("media-documents")
                .upload(path, file, {
                    // Fresh name every time; never overwrite, or a checked and
                    // attached file could be swapped under the same key.
                    upsert: false,
                    contentType: kind.contentType,
                });
            if (uploadError) throw new Error(uploadError.message);

            // Content check (real format, no active PDF content), the same one an
            // organization's upload goes through. A refused file is deleted by the
            // function; the database only accepts a key with a clean verdict
            // (AGREEMENT_NOT_VERIFIED).
            const { data: verdict, error: verifyError } = await supabase.functions.invoke(
                "verify-media-document",
                { body: { path } }
            );
            const failure = verifyError
                ? await mediaErrorFromInvoke(verifyError)
                : mediaErrorFromBody(verdict);
            if (failure) {
                // A refusal (DOCUMENT_REJECTED) is deleted by the function; any
                // other failure would leave an unchecked file behind.
                if (failure.code !== "DOCUMENT_REJECTED") {
                    await supabase.storage.from("media-documents").remove([path]);
                }
                throw failure;
            }

            const { data, error } = await supabase
                .from("media_organizations")
                .update({
                    agreement_url: path,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", id)
                .select()
                .single();

            if (error) {
                if (error.message.includes("AGREEMENT_NOT_VERIFIED")) {
                    throw new MediaFunctionError("AGREEMENT_NOT_VERIFIED", error.message);
                }
                throw new Error(error.message);
            }
            return data;
        },
        onSuccess: () => {
            // The agrément document backs per-league access (see hooks/mediaInvalidation.ts).
            ORG_ACCESS_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

/**
 * Provision (or reset) the organization's Supabase login via the
 * `provision-org-account` edge function. Returns the credentials once.
 */
export function useProvisionOrgLogin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            organizationId,
            email,
            password,
        }: {
            organizationId: string;
            email?: string;
            password?: string;
        }): Promise<ProvisionOrgLoginResult> => {
            const { data, error } = await supabase.functions.invoke(
                "provision-org-account",
                {
                    body: {
                        organization_id: organizationId,
                        ...(email ? { email } : {}),
                        ...(password ? { password } : {}),
                    },
                }
            );

            if (error) throw new Error(await messageFromInvokeError(error));
            const bodyError = messageFromInvokeBody(data);
            if (bodyError) throw new Error(bodyError);
            return data as ProvisionOrgLoginResult;
        },
        onSuccess: () => {
            // Provisioning flips the org's usable-account state that the
            // per-league access views read (see hooks/mediaInvalidation.ts).
            ORG_ACCESS_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

/**
 * Create a short-lived signed URL to view/download the agrément PDF from the
 * private bucket.
 */
export async function getAgreementSignedUrl(
    agreementUrl: string | null | undefined
): Promise<string | null> {
    if (!agreementUrl) return null;
    // Already a full URL (legacy) → return as-is.
    if (agreementUrl.startsWith("http")) return agreementUrl;

    const { data, error } = await supabase.storage
        .from("media-documents")
        .createSignedUrl(agreementUrl, 60);

    if (error) throw new Error(error.message);
    return data?.signedUrl ?? null;
}
